/**
 * Theme & Brand Color Management Utility
 * 
 * Supports defining a primary brand/theme color in .env:
 * - REACT_APP_THEME_COLOR (e.g. #0ea5e9, #6366f1, #3b82f6, #10b981, #ec4899, #8b5cf6, etc.)
 * - Optional runtime fallback from /api/auth-config (if specified on server)
 * 
 * Automatically computes shades (50-950), RGB channels for alpha opacities,
 * ambient glow colors, and injects CSS custom properties into :root.
 */

function hexToRgb(hex) {
    if (!hex) return null;
    let clean = hex.replace('#', '').trim();
    if (clean.length === 3) {
        clean = clean.split('').map(c => c + c).join('');
    }
    if (clean.length !== 6) return null;
    const num = parseInt(clean, 16);
    if (isNaN(num)) return null;
    return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255,
    };
}

function rgbToHex(r, g, b) {
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
    return `#${[r, g, b].map(x => clamp(x).toString(16).padStart(2, '0')).join('')}`;
}

// Blend color with white (tint) or black (shade)
function blend(rgb, targetRgb, factor) {
    return {
        r: rgb.r + (targetRgb.r - rgb.r) * factor,
        g: rgb.g + (targetRgb.g - rgb.g) * factor,
        b: rgb.b + (targetRgb.b - rgb.b) * factor,
    };
}

/**
 * Generates a full Tailwind-compatible 50-950 scale from a base hex color.
 * 500 is the base color. 50..400 are tints towards white. 600..950 are shades towards black.
 */
export function generateColorShades(baseHex = '#0ea5e9') {
    const rgb = hexToRgb(baseHex) || hexToRgb('#0ea5e9');
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };

    return {
        50: rgbToHex(...Object.values(blend(rgb, white, 0.92))),
        100: rgbToHex(...Object.values(blend(rgb, white, 0.82))),
        200: rgbToHex(...Object.values(blend(rgb, white, 0.64))),
        300: rgbToHex(...Object.values(blend(rgb, white, 0.44))),
        400: rgbToHex(...Object.values(blend(rgb, white, 0.22))),
        500: rgbToHex(rgb.r, rgb.g, rgb.b),
        600: rgbToHex(...Object.values(blend(rgb, black, 0.16))),
        700: rgbToHex(...Object.values(blend(rgb, black, 0.34))),
        800: rgbToHex(...Object.values(blend(rgb, black, 0.52))),
        900: rgbToHex(...Object.values(blend(rgb, black, 0.70))),
        950: rgbToHex(...Object.values(blend(rgb, black, 0.84))),
    };
}

/**
 * Apply brand theme variables and background colors to document root
 * @param {string} [customHex] - Primary brand theme color (defaults to REACT_APP_THEME_COLOR or #0ea5e9)
 * @param {string} [customBgHex] - Explicit page background color (defaults to REACT_APP_BG_COLOR or #050507)
 * @param {string} [customCardBgHex] - Explicit card background color (defaults to REACT_APP_CARD_BG_COLOR or #121318)
 */
export function applyThemeColor(customHex, customBgHex, customCardBgHex) {
    if (typeof document === 'undefined') return;

    const envColor = process.env.REACT_APP_THEME_COLOR;
    const colorToApply = (customHex || envColor || '#0ea5e9').trim();
    const rgb = hexToRgb(colorToApply) || { r: 14, g: 165, b: 233 };
    const shades = generateColorShades(colorToApply);

    const root = document.documentElement;

    // Set CSS custom variables for primary brand
    root.style.setProperty('--color-primary-hex', shades[500]);
    root.style.setProperty('--color-primary-rgb', `${rgb.r} ${rgb.g} ${rgb.b}`);

    // Set individual shades for CSS/Tailwind variables
    Object.entries(shades).forEach(([shade, hexVal]) => {
        const sRgb = hexToRgb(hexVal);
        root.style.setProperty(`--color-primary-${shade}`, hexVal);
        if (sRgb) {
            root.style.setProperty(`--color-primary-${shade}-rgb`, `${sRgb.r} ${sRgb.g} ${sRgb.b}`);
        }
    });

    // Ambient glow colors
    root.style.setProperty('--theme-glow-rgba', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4)`);
    root.style.setProperty('--theme-glow-subtle', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`);

    // 1. Dedicated Page Background Color Parameter (REACT_APP_BG_COLOR / BG_COLOR)
    const envBg = process.env.REACT_APP_BG_COLOR;
    const rawBg = (customBgHex || envBg || '').trim();
    
    let bgBaseHex = '#050507';
    let bgRgb = hexToRgb(bgBaseHex);
    let bgRadial;

    if (rawBg) {
        const parsedBgRgb = hexToRgb(rawBg);
        if (parsedBgRgb) {
            bgBaseHex = rawBg;
            bgRgb = parsedBgRgb;
            // Slightly lighter top sheen (or subtle radial depth)
            const bgTopSheen = blend(parsedBgRgb, { r: 255, g: 255, b: 255 }, 0.04);
            bgRadial = `radial-gradient(ellipse at 50% 0%, ${rgbToHex(bgTopSheen.r, bgTopSheen.g, bgTopSheen.b)} 0%, ${bgBaseHex} 100%)`;
        }
    }

    if (!bgRadial) {
        // Deep dark graphite/neutral background
        bgRadial = 'radial-gradient(ellipse at 50% 0%, #0d0e13 0%, #050507 100%)';
    }

    root.style.setProperty('--theme-bg-base', bgBaseHex);
    root.style.setProperty('--theme-bg-radial', bgRadial);

    // 2. Dedicated Card Background Color Parameter (REACT_APP_CARD_BG_COLOR / CARD_BG_COLOR)
    // Allows keeping page background very dark while making cards slightly lighter
    const envCardBg = process.env.REACT_APP_CARD_BG_COLOR;
    const rawCardBg = (customCardBgHex || envCardBg || '').trim();
    
    let cardMidRgb;
    let cardMidHoverRgb;
    let slotMidRgb;
    let slotMidHoverRgb;

    if (rawCardBg) {
        const parsedCardRgb = hexToRgb(rawCardBg);
        if (parsedCardRgb) {
            const cardMidHover = blend(parsedCardRgb, { r: 255, g: 255, b: 255 }, 0.07);
            const slotMid = blend(parsedCardRgb, { r: 0, g: 0, b: 0 }, 0.22);
            const slotMidHover = blend(parsedCardRgb, { r: 255, g: 255, b: 255 }, 0.03);

            cardMidRgb = `${Math.round(parsedCardRgb.r)}, ${Math.round(parsedCardRgb.g)}, ${Math.round(parsedCardRgb.b)}`;
            cardMidHoverRgb = `${Math.round(cardMidHover.r)}, ${Math.round(cardMidHover.g)}, ${Math.round(cardMidHover.b)}`;
            slotMidRgb = `${Math.round(slotMid.r)}, ${Math.round(slotMid.g)}, ${Math.round(slotMid.b)}`;
            slotMidHoverRgb = `${Math.round(slotMidHover.r)}, ${Math.round(slotMidHover.g)}, ${Math.round(slotMidHover.b)}`;
        }
    }

    if (!cardMidRgb) {
        // Default card tone: slightly lighter than background for crisp subtle separation
        const cardMid = blend(bgRgb, { r: 255, g: 255, b: 255 }, 0.07);
        const cardMidHover = blend(bgRgb, { r: 255, g: 255, b: 255 }, 0.12);
        const slotMid = blend(bgRgb, { r: 0, g: 0, b: 0 }, 0.20);
        const slotMidHover = blend(bgRgb, { r: 255, g: 255, b: 255 }, 0.04);

        cardMidRgb = `${Math.round(cardMid.r)}, ${Math.round(cardMid.g)}, ${Math.round(cardMid.b)}`;
        cardMidHoverRgb = `${Math.round(cardMidHover.r)}, ${Math.round(cardMidHover.g)}, ${Math.round(cardMidHover.b)}`;
        slotMidRgb = `${Math.round(slotMid.r)}, ${Math.round(slotMid.g)}, ${Math.round(slotMid.b)}`;
        slotMidHoverRgb = `${Math.round(slotMidHover.r)}, ${Math.round(slotMidHover.g)}, ${Math.round(slotMidHover.b)}`;
    }

    root.style.setProperty('--card-mid-rgb', cardMidRgb);
    root.style.setProperty('--card-mid-hover-rgb', cardMidHoverRgb);
    root.style.setProperty('--slot-mid-rgb', slotMidRgb);
    root.style.setProperty('--slot-mid-hover-rgb', slotMidHoverRgb);

    // Also update document.body directly for instantaneous response
    if (document.body) {
        document.body.style.backgroundColor = bgBaseHex;
        document.body.style.background = bgRadial;
    }
}

/**
 * Get current configured theme color
 */
export function getThemeColor() {
    return process.env.REACT_APP_THEME_COLOR || '#0ea5e9';
}
